/**
 * Helpers that turn an admin email-tracking row (QueuedEmailResource) into plain
 * language, so admins can see what each alert / notification email told the user.
 */

// Words that read better upper-cased when splitting a model class name
const ACRONYMS = [
	'ACRIS', 'BIS', 'CO', 'DEP', 'DOB', 'DOS', 'DOT', 'ECB', 'FDNY', 'HPD',
	'NOV', 'NYC', 'OATH', 'SLA', 'TCO', 'TOO',
];

// Model names whose split-up form would not mean anything to a reader
const LABEL_OVERRIDES = {
	TOOComplaint: '311 Complaint',
};

/**
 * Turns a dataset model class into a readable name,
 * e.g. "App\\Models\\OpenData\\DOB\\Now\\ODDobNowBuildApprovedPermit" -> "DOB Now Build Approved Permit".
 * BIS records get a "(BIS)" suffix so they can be told apart from the Open Data ones.
 *
 * @param {string|null} className The fully qualified model class.
 * @returns {string} The readable name, or "-" when there is no class.
 */
export function getDatasetLabel(className) {
	if (!className) {
		return '-';
	}
	let name = className.split('\\').pop();
	if (LABEL_OVERRIDES[name]) {
		return LABEL_OVERRIDES[name];
	}
	const isBis = /^(BIS|Bis)(?=[A-Z])/.test(name);
	name = name.replace(/^(OD|Od|BIS|Bis|NycGov)(?=[A-Z])/, '');

	const words = name
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.split(' ')
		.filter(Boolean)
		.map((word) => (ACRONYMS.includes(word.toUpperCase()) ? word.toUpperCase() : word));

	return `${words.join(' ')}${isBis ? ' (BIS)' : ''}`;
}

/**
 * Turns a field name such as "violation_status" into "Violation Status".
 *
 * @param {string} field The raw field name.
 * @returns {string} The readable field name.
 */
export function getFieldLabel(field) {
	return String(field)
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.split(' ')
		.filter(Boolean)
		.map((word) => (ACRONYMS.includes(word.toUpperCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
		.join(' ');
}

/**
 * Lists the changed fields of an update email as { field, from, to } rows.
 *
 * @param {object|null} changeData The email's change_data ({ field: { from, to } }).
 * @returns {Array<{field: string, from: string, to: string}>} The changes; empty when there are none.
 */
export function getEmailChanges(changeData) {
	if (!changeData || typeof changeData !== 'object') {
		return [];
	}
	const show = (value) => (value === null || value === undefined || value === '' ? '—' : String(value));
	return Object.entries(changeData).map(([field, change]) => {
		const isFromTo = change && typeof change === 'object' && ('from' in change || 'to' in change);
		return {
			field: getFieldLabel(field),
			from: isFromTo ? show(change.from) : '—',
			to: isFromTo ? show(change.to) : show(change),
		};
	});
}

/**
 * One-sentence summary of what an email told the user.
 *
 * @param {object} email An email-tracking row.
 * @returns {string} The summary.
 */
export function describeEmail(email) {
	const dataset = email?.class ? getDatasetLabel(email.class) : 'record';
	const where = email?.property?.address ? ` at ${email.property.address}` : '';
	const expiringDays = { expiring1: 1, expiring7: 7, expiring14: 14 }[email?.type];

	if (email?.type === 'alert') {
		return `Told the user a new ${dataset} was found${where}, with the record's details and a link to it in Violerts.`;
	}
	if (email?.type === 'notification') {
		const changes = getEmailChanges(email.change_data);
		const fields = changes.map((c) => c.field).join(', ');
		return `Told the user an existing ${dataset}${where} was updated${fields ? ` (${fields})` : ''}.`;
	}
	if (expiringDays) {
		return `Reminded the user that a ${dataset}${where} expires in ${expiringDays} day${expiringDays === 1 ? '' : 's'}.`;
	}
	if (email?.type === 'reminder') {
		return `Reminded the user of an upcoming date for a ${dataset}${where}.`;
	}
	return `Email about a ${dataset}${where}.`;
}
